/**
 * WSJF (Weighted Smallest Job First) tool runner.
 * Implements the WSJF formula: Cost of Delay / Job Duration
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
  AttributePrimitive,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for WSJF.
 */
export function getWsjfSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("wsjf");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the WSJF scoring tool.
 * 
 * Formula: WSJF = Cost of Delay / Job Duration
 * 
 * @param items - Items to score
 * @param _criteriaSet - CriteriaSet (optional, not used by WSJF)
 * @param _toolConfig - Tool configuration (none required)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runWsjfTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  _toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `wsjf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    // Use the arrange validation applyLens function
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Score each item
  const scoredItems: Array<{
    item_id: string;
    score: number;
    rank: number;
    cost_of_delay: AttributePrimitive;
    job_duration: AttributePrimitive;
  }> = [];
  const validItems: Array<{ item: Item; score: number }> = [];

  for (const item of filteredItems) {
    const costOfDelay = item.attributes.cost_of_delay;
    const jobDuration = item.attributes.job_duration;

    // Validate required attributes (per TOOL_SPEC: reject if missing or <= 0)
    if (
      costOfDelay === null ||
      costOfDelay === undefined ||
      (typeof costOfDelay === "number" && costOfDelay <= 0)
    ) {
      continue; // Skip invalid items
    }
    if (
      jobDuration === null ||
      jobDuration === undefined ||
      (typeof jobDuration === "number" && jobDuration <= 0)
    ) {
      continue;
    }

    // Calculate WSJF score
    const codNum = typeof costOfDelay === "number" ? costOfDelay : Number(costOfDelay);
    const durationNum = typeof jobDuration === "number" ? jobDuration : Number(jobDuration);
    const score = codNum / durationNum;

    validItems.push({ item, score });
  }

  // Sort by score (descending), then by label (ascending) for tie-breaking
  validItems.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < validItems.length; i++) {
    const { item, score } = validItems[i];
    
    // If score differs from previous, update rank
    if (i > 0 && validItems[i - 1].score !== score) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      score: score,
      rank: currentRank,
      cost_of_delay: item.attributes.cost_of_delay,
      job_duration: item.attributes.job_duration,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: validItems.length,
    },
  };
}

